import { useEffect } from 'react'

import { usePrivy } from '@privy-io/react-auth'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useDisconnect } from 'wagmi'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { dialogTitleClassName } from '~/components/smart-accounts/TransactionDetailDialogContent'
import { TransactionDisplay } from '~/components/txn-display'
import { useConnectedAddress } from '~/hooks/useConnectedAddress'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useOrgTransaction } from '~/hooks/useTransactions'
import { useDialogRoutePath } from '~/hooks/utils'
import { OrgTransaction } from '~/types'
import { connectedAddressIsEoaSigner } from '~/utils/smartAccounts/signingReadiness'

export const ApproveTransactionDialogContent = ({
  transaction: transactionProp,
  onClose,
  globalModal,
  onConfirmation,
  onSubmitError,
  closeOnSubmit,
  skipRoutePath,
}: {
  transaction: OrgTransaction
  onClose: () => void
  globalModal?: boolean
  onConfirmation?: () => void
  onSubmitError?: (e: unknown) => void
  closeOnSubmit?: boolean
  // Keep the browser path as it is. Callers that open this dialog from a page
  // the user should stay on opt out of the deep link, because restoring the
  // path on close walks the history stack and can land somewhere else.
  skipRoutePath?: boolean
}) => {
  const { transaction: latestTransaction } = useOrgTransaction(
    transactionProp.id,
  )
  const transaction = latestTransaction ?? transactionProp

  // Connect dialog is unclickable while this dialog is open because Radix
  // sets pointer-events:none on <body>. Instead of fixing z-indices (which
  // depends on rainbow's implementation), hide this dialog while the connect
  // modal is open — same pattern as EOASignerVerificationDialog.
  const { connectModalOpen } = useConnectModal()

  const { smartAccounts } = useSmartAccounts({
    includeRootAccount: true,
  })
  const smartAccount = smartAccounts.find(
    (sa) => sa.id === transaction.smartAccountId,
  )!

  // Auto-disconnect a stale wallet connection when the dialog opens for an
  // account with EOA signers (e.g. recovery). This ensures the user is
  // immediately prompted to connect the correct recovery signer rather than
  // seeing a confusing disabled state from a stale connection. Skip when the
  // user is already authenticated with Privy — the dialog remounts after the
  // Privy login UI closes, and disconnecting here would race the embedded
  // wallet that usePrivyWalletBridge is activating for signing. Likewise skip
  // when the connected wallet is one of the account's EOA signers: the dialog
  // also remounts after RainbowKit closes, right after that signer connected.
  const { disconnect } = useDisconnect()
  const { authenticated: isPrivyAuthenticated } = usePrivy()
  const connectedAddress = useConnectedAddress()
  useEffect(() => {
    if (
      smartAccount?.eoaSigners.length > 0 &&
      !isPrivyAuthenticated &&
      !connectedAddressIsEoaSigner({ connectedAddress, smartAccount })
    ) {
      disconnect()
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDialogRoutePath({
    globalModal,
    closeModal: onClose,
    getPath: () => {
      if (skipRoutePath) return
      if (transaction && smartAccount) {
        return `/accounts/${smartAccount.address}/transactions/${transaction.id}/`
      }
    },
  })

  if (connectModalOpen) {
    return null
  }

  return (
    <SplitsDialogContent
      id="approveTransaction"
      title={
        <TransactionTitle
          transaction={transaction}
          className={dialogTitleClassName}
          textWrap
        />
      }
      variant="info"
      clampTitle={false}
    >
      <TransactionDisplay
        transaction={transaction}
        onClose={closeOnSubmit ? onClose : undefined}
        onConfirmation={onConfirmation}
        onSubmitError={onSubmitError}
      />
    </SplitsDialogContent>
  )
}
